import React, { useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import DownloadIcon from '@mui/icons-material/Download';
import { toBlob } from 'html-to-image';
import { saveAs } from 'file-saver';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { showAlertError } from '@utils/Alerts/Error';
import { TicketImage, TicketImageProps, TICKET_WIDTH } from './TicketImage';

// El DIÁLOGO que envuelve al lienzo (`TicketImage`) y lo comparte
// (PLAN-REDISENO-PANEL-POS-Y-RECIBO, D6). Dos consumidores: el POS 2.0 antes de
// cobrar (sin folio) y la re-impresión del tablero (con folio). **El ticket no se
// etiqueta según el caso** (ajuste de Irving): es el mismo y se manda para muchas
// cosas.
//
// COMPARTIR de verdad: el propósito de esto es mandarlo por WhatsApp. Antes el
// botón solo DESCARGABA (y con extensión .jpg siendo un PNG), así que en la
// tablet el cajero tenía que ir a buscar el archivo. Ahora se usa Web Share con
// el archivo — en la tablet abre WhatsApp directo — y la descarga queda como
// respaldo donde no exista.
interface ModalShowTicketProps extends TicketImageProps {
    modalShowTicket: OpenModalHookType;
}

// `navigator.canShare` no está en el lib DOM de esta versión de TS
const canShareFiles = (files: File[]): boolean => {
    const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean };
    return typeof nav.canShare === 'function' && typeof nav.share === 'function' && nav.canShare({ files });
};

export const ModalShowTicket: React.FunctionComponent<ModalShowTicketProps> = (props) => {
    const { modalShowTicket, ...ticket } = props;
    const ticketRef = useRef<HTMLDivElement | null>(null);
    const [busy, setBusy] = useState<boolean>(false);

    const handleClose = () => modalShowTicket.toggle();

    const buildImage = async (): Promise<{ blob: Blob; file: File } | undefined> => {
        if (!ticketRef.current) return undefined;
        // pixelRatio 2: la imagen se ve nítida en el teléfono del cliente
        const blob = await toBlob(ticketRef.current, {
            cacheBust: true,
            backgroundColor: '#FFFFFF',
            pixelRatio: 2
        });
        if (!blob) return undefined;
        // Antes se guardaba como .jpg siendo PNG (`toBlob` produce PNG)
        const name = `krispis-${ticket.shortId || 'pedido'}.png`;
        return { blob, file: new File([blob], name, { type: 'image/png' }) };
    };

    const onShare = async () => {
        setBusy(true);
        try {
            const image = await buildImage();
            if (!image) return;
            if (canShareFiles([image.file])) {
                try {
                    await navigator.share({
                        files: [image.file],
                        title: ticket.shortId ? `Recibo ${ticket.shortId}` : 'Tu pedido Krispis'
                    });
                    return;
                } catch {
                    // El usuario canceló el compartir: no es un error
                    return;
                }
            }
            // Sin Web Share (navegador de escritorio): queda la descarga
            saveAs(image.blob, image.file.name);
        } catch (error: any) {
            showAlertError('No se pudo generar la imagen del recibo');
        } finally {
            setBusy(false);
        }
    };

    const onDownload = async () => {
        setBusy(true);
        try {
            const image = await buildImage();
            if (image) saveAs(image.blob, image.file.name);
        } catch (error: any) {
            showAlertError('No se pudo generar la imagen del recibo');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            PaperProps={{ style: { borderRadius: 15 } }}
            open={modalShowTicket.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-ticket-title"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="modal-ticket-title" sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={700}>
                        Recibo
                    </Typography>
                    <IconButton aria-label="Cerrar" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: 'background.default', display: 'flex', justifyContent: 'center', py: 2 }}>
                {/* El lienzo se pinta a su ancho FIJO y centrado; si la pantalla es
                    más angosta, scrollea en vez de deformar la imagen */}
                <Box sx={{ minWidth: TICKET_WIDTH, boxShadow: 1, borderRadius: 1, overflow: 'hidden' }}>
                    <TicketImage ref={ticketRef} {...ticket} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={1} width="100%">
                    {/* ⚠️ `color="primary"`, NO `secondary`: en este tema
                        `palette.secondary.main` es #FFFFFF puro, así que un
                        outlined secondary queda con texto y borde BLANCOS sobre el
                        diálogo blanco — invisible. Es el mismo bug del botón
                        "Gasto" de la calculadora (D5 del plan), reintroducido aquí
                        por descuido y detectado por Irving. */}
                    <Button
                        fullWidth
                        color="primary"
                        variant="outlined"
                        disabled={busy}
                        startIcon={<DownloadIcon />}
                        onClick={onDownload}
                    >
                        Descargar
                    </Button>
                    <Button
                        fullWidth
                        color="primary"
                        variant="contained"
                        disabled={busy}
                        startIcon={<IosShareIcon />}
                        onClick={onShare}
                    >
                        Compartir
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
